import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Search, AlertTriangle } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";

const inp = "w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#D62B3A] outline-none text-sm";

export function AllergenAdmin() {
  const [catalog, setCatalog] = useState([]);
  const [maps, setMaps] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(null);
  const [pending, setPending] = useState([]);

  const load = () => api.get(`/admin/allergens/mappings${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((r) => setMaps(r.data));
  useEffect(() => { api.get("/allergens/catalog").then((r) => setCatalog(r.data)); api.get("/admin/allergens/pending").then((r) => setPending(r.data)); }, []);
  useEffect(() => { load(); }, [q]);

  const label = (c) => catalog.find((a) => a.code === c)?.label || c;
  const save = async () => {
    if (!form.keyword.trim()) { toast.error("Stichwort erforderlich"); return; }
    try {
      if (form.id) await api.put(`/admin/allergens/mappings/${form.id}`, { keyword: form.keyword, codes: form.codes });
      else await api.post("/admin/allergens/mappings", { keyword: form.keyword, codes: form.codes });
      toast.success("Gespeichert"); setForm(null); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Zuordnung löschen?")) return; await api.delete(`/admin/allergens/mappings/${id}`); load(); };
  const toggleCode = (c) => setForm((f) => ({ ...f, codes: f.codes.includes(c) ? f.codes.filter((x) => x !== c) : [...f.codes, c] }));

  return (
    <div className="space-y-8">
      <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800 flex gap-2" data-testid="allergen-disclaimer">
        <AlertTriangle className="w-5 h-5 shrink-0" />
        Automatische Vorschläge dienen der Arbeitserleichterung und ersetzen NICHT die fachliche Prüfung der tatsächlichen Rezeptur. Gluten immer als Unterkategorie 1a–1f.
      </div>

      {/* Massenprüfung */}
      <div>
        <h3 className="font-bold mb-2">Massenprüfung · unbestätigte Gerichte ({pending.length})</h3>
        <div className="bg-white rounded-xl border border-gray-100 divide-y">
          {pending.map((p) => (
            <div key={p.id} className="flex items-center gap-3 px-4 py-2.5 text-sm" data-testid={`allergen-pending-${p.id}`}>
              <span className="flex-1 font-medium">{p.name}</span>
              <span className="text-gray-400 text-xs">{(p.allergen_codes || []).map((c) => `#${c}`).join(" ") || "—"}</span>
              <span className="text-xs font-semibold text-amber-600">nicht bestätigt</span>
            </div>
          ))}
          {!pending.length && <div className="px-4 py-6 text-center text-gray-400 text-sm">Alle Gerichte sind bestätigt. 🎉</div>}
        </div>
      </div>

      {/* Mapping-Tabelle */}
      <div>
        <div className="flex items-center justify-between mb-3 gap-3">
          <h3 className="font-bold">Zutat → Allergen Zuordnungstabelle</h3>
          <button onClick={() => setForm({ keyword: "", codes: [] })} data-testid="mapping-add-btn" className="inline-flex items-center gap-2 bg-[#D62B3A] text-white text-sm font-semibold px-4 py-2 rounded-lg"><Plus className="w-4 h-4" /> Zuordnung</button>
        </div>
        <div className="relative mb-3 max-w-sm">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
          <input className={inp + " pl-8"} placeholder="Stichwort suchen…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="mapping-search" />
        </div>
        <div className="bg-white rounded-xl border border-gray-100 divide-y max-h-[420px] overflow-y-auto">
          {maps.map((m) => (
            <div key={m.id} className="flex items-center gap-3 px-4 py-2.5 text-sm" data-testid={`mapping-row-${m.id}`}>
              <span className="font-semibold flex-1">{m.keyword}</span>
              <span className="flex flex-wrap gap-1">{m.codes.map((c) => <span key={c} className="bg-[#6F6F6F] text-white text-xs px-2 py-0.5 rounded-full">#{c}-{label(c)}</span>)}</span>
              <button onClick={() => setForm(m)} className="p-1.5 rounded hover:bg-gray-100"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => del(m.id)} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {!maps.length && <div className="px-4 py-6 text-center text-gray-400 text-sm">Keine Zuordnungen.</div>}
        </div>
      </div>

      {form && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setForm(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4"><h3 className="font-bold">{form.id ? "Zuordnung bearbeiten" : "Neue Zuordnung"}</h3><button onClick={() => setForm(null)}><X className="w-5 h-5" /></button></div>
            <label className="text-xs text-gray-500">Stichwort (Zutat/Gericht)</label>
            <input className={inp} value={form.keyword} onChange={(e) => setForm({ ...form, keyword: e.target.value })} data-testid="mapping-keyword" placeholder="z. B. sahne" />
            <div className="mt-3 text-xs text-gray-500 mb-1">Allergene</div>
            <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto">
              {catalog.map((a) => (
                <button key={a.code} type="button" onClick={() => toggleCode(a.code)} data-testid={`mapping-code-${a.code}`}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium ${form.codes.includes(a.code) ? "bg-[#6F6F6F] text-white" : "bg-gray-50 text-gray-600 border border-gray-200"}`}>#{a.code}-{a.label}</button>
              ))}
            </div>
            <button onClick={save} data-testid="mapping-save-btn" className="mt-5 w-full bg-[#D62B3A] text-white font-semibold py-2.5 rounded-lg">Speichern</button>
          </div>
        </div>
      )}
    </div>
  );
}
